import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, SearchX } from "lucide-react";
import { toast } from "sonner";
import {
  PhotoListItem,
  PhotoListItemSkeleton,
} from "@/components/records/photo-list-item/photo-list-item";
import { readMetadataByFieldName } from "@/components/common/image-viewer-modal/use-submit-record";
import { RaiseIssueSheet } from "@/components/issues/raise-issue-sheet/raise-issue-sheet";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { outboxPlaceholderRecord } from "@/hooks/use-pending-records";
import { useRecordCells } from "@/hooks/use-record-cells";
import type { RecordFilters } from "@/lib/query-keys";
import type {
  ProjectMetadataField,
  ProjectPlan,
  ProjectRecord,
} from "@/types/api";

/**
 * Row height in px: a 64px thumbnail inside 6px padding, plus the 1px hairline
 * to the next row. Fixed so the virtualizer never has to measure.
 */
const ROW_HEIGHT = 77;
/** Name of the metadata field rendered as a row's description. */
const DESCRIPTION_FIELD = "remarks";

interface PhotoListProps {
  projectId: string;
  /** Id of the record open in the side panel, marked in the gallery. */
  activeRecordId?: string | null;

  filters?: RecordFilters;
  /** Project metadata fields — used to resolve each record's description. */
  metadataFields?: ProjectMetadataField[];
  /** Project plans — used to resolve a record's plan id to a name. */
  plans?: ProjectPlan[];
  onRecordClick?: (record: ProjectRecord) => void;
  isSelectionMode?: boolean;
  selectedIds?: ReadonlyArray<string>;
  onToggleSelection?: (record: ProjectRecord) => void;
  onLongPressRecord?: (record: ProjectRecord) => void;
  onItemsChange?: (records: ProjectRecord[]) => void;
  /**
   * Whether the viewer may raise issues (`issue:Create`). Gates the
   * swipe-left-to-raise shortcut on a row, which opens the same sheet the
   * record viewer does. See [[photo-list.spec]] US-006.
   */
  canRaiseIssue?: boolean;
}

/**
 * The row-per-record presentation of the photos page — the alternative to
 * [[photo-grid.spec]], chosen from the toggle in [[project-header.spec]]. Reads
 * the same record source as the grid, so the two can never disagree about what
 * a filter returns. See [[photo-list.spec]].
 */
export function PhotoList({
  projectId,
  filters,
  metadataFields,
  plans,
  onRecordClick,
  isSelectionMode = false,
  selectedIds,
  onToggleSelection,
  onLongPressRecord,
  onItemsChange,
  canRaiseIssue = false,
  activeRecordId = null,
}: PhotoListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // The record a swipe is raising an issue against — one sheet for the list,
  // opened with whichever row committed the gesture.
  const [issueRecord, setIssueRecord] = useState<ProjectRecord | null>(null);
  // Swipe is a thumb gesture: mobile only, matching where the toggle that
  // chose this view lives. Desktop keeps the record viewer's own entry point.
  const isDesktop = useIsDesktop();
  const swipeToRaise = canRaiseIssue && !isDesktop && !isSelectionMode;

  const {
    cells,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isError,
    refetch,
    offlineCapReached,
    cachedCount,
    totalCount,
    isFilteredOffline,
    retryOutbox,
    appendStateFor,
  } = useRecordCells(projectId, filters, onItemsChange);

  const planNameById = useMemo(
    () => new Map((plans ?? []).map((plan) => [plan.id, plan.name])),
    [plans],
  );
  const selectedIdSet = useMemo(
    () => new Set(selectedIds ?? []),
    [selectedIds],
  );

  const rowVirtualizer = useVirtualizer({
    count: cells.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 6,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const lastVirtualRow = virtualRows[virtualRows.length - 1];

  useEffect(() => {
    if (!lastVirtualRow) return;
    if (!hasNextPage || isFetchingNextPage) return;
    if (lastVirtualRow.index >= cells.length - 5) {
      fetchNextPage();
    }
  }, [
    lastVirtualRow,
    hasNextPage,
    isFetchingNextPage,
    cells.length,
    fetchNextPage,
  ]);

  if (isLoading) {
    return (
      <div className="h-full w-full overflow-auto p-2" aria-busy="true">
        <div className="divide-y divide-border overflow-hidden rounded-[10px] border border-border bg-card">
          {Array.from({ length: 8 }).map((_, i) => (
            <PhotoListItemSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Gate the error fallback on the absence of cached data, exactly as the grid
  // does: TanStack Query preserves last-good `data` when a refetch throws, so a
  // pinned project opened offline must keep showing its rows.
  // See [[photo-grid.spec]] US-005 / Stale-with-refresh-error.
  if (isError && cells.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <div className="text-center">
          <p className="mb-4 text-sm text-muted-foreground">
            Failed to load records.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (cells.length === 0) {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center gap-3 p-8"
        data-testid="records-filtered-empty"
      >
        <SearchX className="h-12 w-12 text-muted-foreground/50" />
        <div className="text-center">
          <p className="text-lg font-medium text-muted-foreground">
            No photos found
          </p>
          <p className="text-sm text-muted-foreground/70">
            Try adjusting your filters or date range
          </p>
          {isFilteredOffline && (
            <p className="text-sm text-muted-foreground/70">
              Only photos saved for offline use are searched — reconnect to
              filter all.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Only the first row that can swipe plays the daily peek; a queued record
  // at the top has no swipe, so it is skipped. The row checks the device has
  // not already seen it. See [[photo-list-item.spec]] US-006.
  const swipeHintIndex = swipeToRaise
    ? cells.findIndex((cell) => cell.kind === "record")
    : -1;

  return (
    <div ref={scrollRef} className="h-full w-full overflow-auto p-2">
      {/* One bordered, rounded block with a hairline between rows, matching
          [[issues-view.spec]]. The rows are absolutely positioned by the
          virtualizer, so each carries its own bottom hairline instead of the
          container using `divide-y`. */}
      <div className="overflow-hidden rounded-[10px] border border-border bg-card">
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: "relative",
            width: "100%",
          }}
        >
          {virtualRows.map((virtualRow) => {
            const cell = cells[virtualRow.index];
            if (!cell) return null;
            // Both kinds render via PhotoListItem. An outbox cell passes its
            // sync-state descriptor + a synthesized placeholder record.
            const record =
              cell.kind === "record"
                ? cell.record
                : outboxPlaceholderRecord(cell.tile);
            const planName = record.plan
              ? (planNameById.get(record.plan) ?? null)
              : null;
            // A queued record has no server id to hang an issue on yet.
            const canSwipeRow = swipeToRaise && cell.kind === "record";
            return (
              <div
                key={record.id}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                className={
                  virtualRow.index < cells.length - 1
                    ? "border-b border-border"
                    : undefined
                }
              >
                <PhotoListItem
                  record={record}
                  description={readMetadataByFieldName(
                    metadataFields ?? [],
                    record.metadata,
                    DESCRIPTION_FIELD,
                  )}
                  planName={planName}
                  outbox={cell.kind === "outbox" ? cell.tile : undefined}
                  onRetryOutbox={retryOutbox}
                  appendState={appendStateFor(cell)}
                  isSelectionMode={isSelectionMode}
                  isSelected={selectedIdSet.has(record.id)}
                  isActive={record.id === activeRecordId}
                  onLongPress={onLongPressRecord}
                  onRaiseIssue={
                    canSwipeRow ? (r) => setIssueRecord(r) : undefined
                  }
                  swipeHint={virtualRow.index === swipeHintIndex}
                  onClick={(r) => {
                    if (isSelectionMode) {
                      onToggleSelection?.(r);
                      return;
                    }
                    onRecordClick?.(r);
                  }}
                />
              </div>
            );
          })}
        </div>
        {isFetchingNextPage && (
          <div className="border-t border-border">
            <PhotoListItemSkeleton />
          </div>
        )}
      </div>
      {/* One sheet for the whole list, bound to whichever row was swiped.
          Same component, same contract as the record viewer's own entry. */}
      {issueRecord && (
        <RaiseIssueSheet
          projectId={projectId}
          recordId={issueRecord.id}
          open
          onOpenChange={(open) => {
            if (!open) setIssueRecord(null);
          }}
          // The record's remarks seed the title, exactly as they do from the
          // record viewer — the row is already showing them as its description.
          defaultTitle={readMetadataByFieldName(
            metadataFields ?? [],
            issueRecord.metadata,
            DESCRIPTION_FIELD,
          )}
          onRaised={() => {
            toast("Issue raised", { icon: <Check className="h-4 w-4" /> });
            setIssueRecord(null);
          }}
        />
      )}
      {offlineCapReached && totalCount > cachedCount && (
        <div
          className="flex items-center justify-center px-4 py-6 text-center text-xs text-muted-foreground"
          data-testid="records-offline-cap-footer"
        >
          {isFilteredOffline
            ? `Filtered the ${cachedCount} of ${totalCount} photos saved offline · reconnect to filter all`
            : `${cachedCount} of ${totalCount} photos available offline · reconnect to load more`}
        </div>
      )}
    </div>
  );
}
